import { AloftPrice, SalesComparisonApproachType } from '@aloft/toolkit-shared';
import { Image, Text, View } from '@react-pdf/renderer';

import { ExportChartContainer } from '../../commentary/export/ExportChartContainer.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { badgeStyles, costApproachPageStyles as styles, fontStyles as fonts, gridStyles } from '../styles.ts';
import { costApproachTableData } from '../types.ts';

const DEPRECIATED_COST_METHODOLOGY_PARAGRAPHS = [
  'Adjustments were derived using the Appraisify Appraiser Toolkit, applying depreciated cost, paired sales, and site value extraction methods. The Replacement Cost New (RCN) was calculated using the Craftsman National Building Cost Manual and compared against comparable sale prices to produce market-extracted adjustments.',
  "Each property's improvements were mapped to Craftsman cost data by square footage and quality level, then depreciated using the age/life method. Site values were extracted for each comparable, reflecting the market contribution of size, location, view, and site improvements (including grading, drains, landscaping, and utilities). The subject's site value was estimated from these extracted values, with site adjustments calculated as the difference between the subject and each comp. Dollars per square foot are rounded to the nearest whole cent.",
  'GLA, bath, quality, condition, basement, parking, and additional feature adjustments (pool, shed, workshop, accessory unit, etc.) were calculated as the difference in depreciated cost between the subject and each comparable, with quality and condition adjustments reflecting differences in cost levels and percent depreciation, respectively.',
  "These adjustments comprehensively account for market value as reflected in comparable sale prices, bounded by well-informed conclusions on site-to-value ratios, effective age, and quality drawn from the appraiser's local market experience.",
];

const fmt = (price: AloftPrice | undefined | null) => price?.toString(true) ?? '—';

type CostApproachSubjectRow = costApproachTableData['subject'];

type CostApproachPageProps = {
  subjectAddress: string;
  costApproachData: costApproachTableData;
  siteSizeVsSiteValueChartImage?: string;
  salesComparisonApproach: SalesComparisonApproachType;
};

const ValuationRows = ({
  s,
  arv,
  hasArv,
}: {
  s: CostApproachSubjectRow;
  arv?: CostApproachSubjectRow;
  hasArv: boolean;
}) => {
  const valueCells = (aiv: AloftPrice | undefined | null, arvPrice?: AloftPrice | null) => (
    <>
      <View style={styles.valueCell}>
        <Text style={fonts.body}>{fmt(aiv)}</Text>
      </View>
      {hasArv && (
        <View style={styles.valueCell}>
          <Text style={fonts.body}>{fmt(arvPrice)}</Text>
        </View>
      )}
    </>
  );

  const valueCellsBold = (aiv: AloftPrice | undefined | null, arvPrice?: AloftPrice | null) => (
    <>
      <View style={styles.valueCell}>
        <Text style={fonts.bodyBold}>{fmt(aiv)}</Text>
      </View>
      {hasArv && (
        <View style={styles.valueCell}>
          <Text style={fonts.bodyBold}>{fmt(arvPrice)}</Text>
        </View>
      )}
    </>
  );

  const valueCellsSmall = (aiv: AloftPrice | undefined | null, arvPrice?: AloftPrice | null) => (
    <>
      <View style={styles.valueCell}>
        <Text style={[fonts.small, fonts.bodyRegularLight]}>{fmt(aiv)}</Text>
      </View>
      {hasArv && (
        <View style={styles.valueCell}>
          <Text style={[fonts.small, fonts.bodyRegularLight]}>{fmt(arvPrice)}</Text>
        </View>
      )}
    </>
  );

  return (
    <>
      <View style={[styles.row, styles.rowIndicatedValue]}>
        <View style={styles.labelCell}>
          <Text style={styles.rollupLabelBold}>Indicated Value by Cost Approach</Text>
        </View>
        {valueCellsBold(s.indicatedValueByCostApproach, arv?.indicatedValueByCostApproach)}
      </View>
      <View style={styles.row}>
        <View style={styles.labelCell}>
          <Text style={styles.rollupLabelBold}>Depreciated Cost of Improvements</Text>
        </View>
        {valueCellsBold(s.depreciatedCostOfImprovement, arv?.depreciatedCostOfImprovement)}
      </View>
      <View style={styles.row}>
        <View style={styles.labelCell}>
          <Text style={[fonts.small, fonts.bodyRegularLight]}>Total Estimate Cost-New</Text>
        </View>
        {valueCellsSmall(s.totalEstimateCostNew, arv?.totalEstimateCostNew)}
      </View>
      <View style={styles.row}>
        <View style={[styles.labelCell, styles.labelCellIndent]}>
          <Text style={fonts.body}>Main Dwelling</Text>
        </View>
        {valueCells(s.dwelling.cost, arv?.dwelling.cost)}
      </View>
      <View style={styles.row}>
        <View style={[styles.labelCell, styles.labelCellIndent]}>
          <Text style={fonts.body}>Garage / Carport</Text>
        </View>
        {valueCells(s.garageCarport.cost, arv?.garageCarport.cost)}
      </View>
      <View style={styles.row}>
        <View style={[styles.labelCell, styles.labelCellIndent]}>
          <Text style={fonts.body}>Basement</Text>
        </View>
        {valueCells(s.basement.cost, arv?.basement.cost)}
      </View>
      <View style={styles.row}>
        <View style={[styles.labelCell, styles.labelCellIndent]}>
          <Text style={fonts.body}>Additional Structures</Text>
        </View>
        {valueCells(s.additionalStructures ?? undefined, arv?.additionalStructures ?? undefined)}
      </View>
      <View style={styles.row}>
        <View style={styles.labelCell}>
          <Text style={[fonts.small, fonts.bodyRegularLight]}>Total Depreciation</Text>
        </View>
        {valueCellsSmall(s.depreciation.total, arv?.depreciation.total)}
      </View>
      <View style={styles.row}>
        <View style={[styles.labelCell, styles.labelCellIndent]}>
          <Text style={fonts.body}>Physical</Text>
        </View>
        {valueCells(s.depreciation.physical, arv?.depreciation.physical)}
      </View>
      <View style={styles.row}>
        <View style={[styles.labelCell, styles.labelCellIndent]}>
          <Text style={fonts.body}>External</Text>
        </View>
        {valueCells(s.depreciation.external, arv?.depreciation.external)}
      </View>
      <View style={styles.row}>
        <View style={[styles.labelCell, styles.labelCellIndent]}>
          <Text style={fonts.body}>Functional</Text>
        </View>
        {valueCells(s.depreciation.functional, arv?.depreciation.functional)}
      </View>
      <View style={[styles.row, styles.rowLast]}>
        <View style={styles.labelCell}>
          <Text style={styles.rollupLabelBold}>Opinion of Site Value</Text>
        </View>
        {valueCellsBold(s.opinionOfSiteValue, arv?.opinionOfSiteValue)}
      </View>
    </>
  );
};

export const CostApproachPage = ({
  subjectAddress,
  costApproachData,
  siteSizeVsSiteValueChartImage,
}: CostApproachPageProps) => {
  const { subject, subjectArv } = costApproachData;
  const hasArv = Boolean(subjectArv);

  const metadata = (s: CostApproachSubjectRow) => (
    <>
      <View style={gridStyles.row}>
        <View style={gridStyles.listItemDotSmall} />
        <Text style={fonts.body}>Source of cost data: Craftsman</Text>
      </View>
      <View style={gridStyles.row}>
        <View style={gridStyles.listItemDotSmall} />
        <Text
          style={fonts.body}
        >{`Effective date of cost data: ${s.effectiveDate ? `01/01/${s.effectiveDate}` : 'unknown'}`}</Text>
      </View>
      <View style={gridStyles.row}>
        <View style={gridStyles.listItemDotSmall} />
        <Text
          style={fonts.body}
        >{`Quality rating from cost service: ${s.qualityRating ? s.qualityRating.toFixed(2) : 'unknown'}`}</Text>
      </View>
      <View style={gridStyles.row}>
        <View style={gridStyles.listItemDotSmall} />
        <Text
          style={fonts.body}
        >{`Est. remaining economic life: ${s.remainingEconomicLife ? s.remainingEconomicLife : 0} years`}</Text>
      </View>
    </>
  );

  return (
    <Tkv2PDFPage title="Cost Approach" address={subjectAddress}>
      <View style={styles.twoColumnRow}>
        <View style={styles.tableColumn}>
          {hasArv ? (
            <View style={styles.tableHeaderRow}>
              <View style={styles.tableHeaderLabelCol}>
                <Text style={[fonts.accent, fonts.uppercase, fonts.small]}>Valuation</Text>
              </View>
              <View style={styles.tableHeaderBadgeCol}>
                <Text style={[badgeStyles.compact, badgeStyles.aiv]}>AIV</Text>
              </View>
              <View style={styles.tableHeaderBadgeCol}>
                <Text style={[badgeStyles.compact, badgeStyles.arv]}>ARV</Text>
              </View>
            </View>
          ) : (
            <View style={styles.tableHeaderRow}>
              <View style={styles.tableHeaderLabelCol}>
                <Text style={[fonts.accent, fonts.uppercase, fonts.small]}>Valuation</Text>
              </View>
              <View style={styles.tableHeaderBadgeCol}>
                <Text style={[fonts.bodyLight, styles.tableHeaderValueText]}>Value</Text>
              </View>
            </View>
          )}
          <View style={styles.tableBody}>
            <ValuationRows s={subject} arv={subjectArv} hasArv={hasArv} />
          </View>
        </View>

        <View style={styles.chartColumn}>
          <View style={styles.chartBlock} wrap={false}>
            <ExportChartContainer title="Site Size vs Site Value">
              <Image src={siteSizeVsSiteValueChartImage} style={styles.chartImage} />
            </ExportChartContainer>
          </View>
          {metadata(subject)}
        </View>
      </View>

      <View style={styles.methodologySection}>
        <Text style={fonts.h4}>Depreciated Cost Methodology</Text>
        {DEPRECIATED_COST_METHODOLOGY_PARAGRAPHS.map((paragraph) => (
          <Text key={paragraph} style={fonts.body}>
            {paragraph}
          </Text>
        ))}
      </View>
    </Tkv2PDFPage>
  );
};
